import React, {useEffect, useState, useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {curry} from 'ramda';
import Box from '@material-ui/core/Box';
import useQuery from '@pluto/query-hook';
import {getNewSort} from '@pluto/qc-sort';
import {setQuery} from '@pluto/set-history-query';
import Table, {
    getInitialRowsPerPage,
    SortingOptionsValues,
    getInitialPage,
    getInitialSort,
    Sort,
} from '@pluto/table';
import {selectStatusesObject} from 'ContentItem/Search/Redux/SearchSelectors';
import {baseRoutes} from 'App/AppRouter/routes';
import {TabsEnum} from '../ContentItemViewTypes';
import {selectContentItemViewWorkHistory} from './Redux/ContentItemViewWorkHistorySelectors';
import {
    getContentItemWorkHistoryAction as setList,
    resetContentItemWorkHistoryAction as resetList,
} from './Redux/ContentItemViewWorkHistoryActions';
import {ContentItemViewWorkHistory} from './ContentItemViewWorkHistoryTypes';
import './ContentItemViewWorkHistory.scss';
import {Statuses} from '../../ContentItem/Search/SearchTypes';

export const getInitialWorkHistorySort = (query: URLSearchParams): Sort => {
    const initialSort = getInitialSort(query);

    if (initialSort.sortBy) {
        return initialSort;
    }

    return {
        sortBy: 'createdAt',
        sortOrder: SortingOptionsValues.desc,
    };
};

export const handlePaging = curry((containerArgs, tablePaging) => {
    const {
        setList,
        setQuery,
        history,
        route,
        id,
        dispatch,
        tab,
        ...containerParams
    } = containerArgs;

    const params = {
        ...containerParams,
        ...tablePaging,
    };

    setQuery(history, route, {
        ...params,
        tab,
    });

    dispatch(
        setList({
            id,
            params,
        })
    );
});

export const handleSort = curry((componentParams, oldSort) => {
    const {
        setSort,
        setQuery,
        history,
        route,
        dispatch,
        id,
        setList,
        tab,
        ...restParams
    } = componentParams;

    const newSort = getNewSort(oldSort);
    setSort(newSort);

    const params = {
        ...restParams,
        sortBy: newSort.sortBy,
        sortOrder: newSort.sortOrder,
    };

    setQuery(history, route, {
        ...params,
        tab,
    });

    dispatch(
        setList({
            id,
            params,
        })
    );
});

export const getStatusColor = (item: ContentItemViewWorkHistory, statuses: Statuses) => {
    const {status} = item;

    switch (status) {
        case 'DELIVERY_COMPLETED':
        case 'QC_PASSED':
        case 'INGEST_COMPLETED':
            return <div className="status-green">{statuses[status]}</div>;
        default:
            return <div className="status-white">{statuses[status]}</div>;
    }
};

export const renderStatusColor = (statuses: Statuses) => (
    item: ContentItemViewWorkHistory
) => {
    return getStatusColor(item, statuses);
};

export default function ContentItemViewWorkHistoryContainer({id}: {id: string}) {
    const contentItemViewWorkHistory = useSelector(selectContentItemViewWorkHistory);
    const query = useQuery();
    const initialSort = getInitialWorkHistorySort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    const history = useHistory();
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const [page, setPage] = useState<number>(initialPage);

    const offset = rowsPerPage * page;

    const dispatch = useDispatch();

    const statuses = useSelector(selectStatusesObject) as Statuses;

    const route = `${baseRoutes.contentItemsSearch}/${id}`;

    const params = useMemo(
        () => ({
            offset,
            limit: rowsPerPage,
            ...sort,
            tab: TabsEnum.workHistory,
            route,
            setQuery,
            history,
            dispatch,
            id,
            setList,
        }),
        [offset, rowsPerPage, sort, route, id]
    );

    useEffect(() => {
        dispatch(
            setList({
                id,
                params: {
                    ...sort,
                    limit: rowsPerPage,
                    offset,
                },
            })
        );
        return () => {
            dispatch(resetList());
        };
    }, []);

    const onSort = handleSort({
        setSort,
        ...params,
    });

    const onPaging = handlePaging(params);

    return (
        <Box className="work-history">
            <Table
                sort={sort}
                setSort={onSort}
                data={contentItemViewWorkHistory}
                columns={tableColumns(statuses)}
                setPage={setPage}
                page={page}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                onChangePageHandler={onPaging}
            />
        </Box>
    );
}

export const tableColumns = (statuses: Statuses) => [
    {
        dataKey: 'status',
        title: 'Workflow State',
        render: renderStatusColor(statuses),
    },
    {
        dataKey: 'createdAt',
        title: `Date`,
        sortable: true,
    },
    {
        dataKey: 'jobType',
        title: 'Job Type',
    },
    {
        dataKey: 'jobId',
        title: 'Job ID',
    },
];
